import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { useState, useEffect } from "react";

import { states } from "js/placeholderData/states";
import {
  getAllOwnersFromParties,
  isEntityUser,
  isPolicyInactiveByPolicyStatus,
} from "js/utilities";
import { getPoliciesOptions } from "js/formatting";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import SSNInput from "components/General/Form/SSNInput";
import FormInputError from "components/General/FormInputError";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";

export default function FACStepOne({
  selectedPolicy,
  setSelectedPolicy,
  formStep,
  setFormStep,
  setFACFormData,
  setLoggedUser,
}) {
  let debounceFunction; //timer identifier
  let debounceTimer = 1500; //time in ms, 1.5 seconds for example
  const uuid = uuidv4();
  const { user } = useSelector((s) => s.user);
  const { policies } = useSelector((s) => s.data);
  const { t } = useTranslation();

  const [policiesToChooseFrom, setPoliciesToChooseFrom] = useState([]);
  const [isDateOfDeathValid, setIsDateOfDeathValid] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);

  const [isCorporationUserLoggedIn, setIsCorporationUserLoggedIn] =
    useState(false);
  const [selectedOption, setSelectedOption] = useState({
    value: "all",
    displayAs: `${t("CustomerClaims-policy-option")}`,
    selectedPolicyDisplayAs: `${t("CustomerClaims-policy-option")}`,
  });
  const [selectedState, setSelectedState] = useState({
    value: "all",
  });

  const {
    register: FACStepOneRegister,
    setValue,
    getValues,
    watch,
    handleSubmit: FACStepOneHandleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    // configurate login user for policies to choose from
    if (user.userId) {
      const policySelectionArray = getPoliciesOptions(policies);
      let policiesOptions = [];

      policySelectionArray.map((policy) => {
        policiesOptions.push({
          ...policy,
          selectedPolicyDisplayAs: `${policy.value}`,
          contractID: policies?.find((p) => p.oldPolicy === policy.value)
            ?.contractID,
          isInActive: isPolicyInactiveByPolicyStatus(policy?.status),
          status: policy?.status,
        });
      });
      setPoliciesToChooseFrom([selectedOption, ...policiesOptions]);

      // default customer portal policyID if there are only one
      if (policiesOptions.length === 1) {
        setSelectedOption(policiesOptions[0]);
        setValue("policyNumber", policiesOptions[0].contractID);
        setSelectedPolicy(policies[0]);
        if (isPolicyInactiveByPolicyStatus(policies[0].status)) {
          setIsDisabled(true);
          setOpenDialog(true);
          setSelectedPolicy({});
        }
      }
    }
  }, [user, policies]);

  useEffect(() => {
    setIsDisabled(false);
    if (
      selectedOption?.value &&
      selectedOption?.value !== "all" &&
      !selectedOption?.isInActive
    ) {
      let policy = policies?.find(
        (p) => p.contractID === selectedOption?.contractID
      );
      setValue("policyNumber", selectedOption.contractID);
      fetchDataPolicyData(selectedOption.contractID);
      if (isPolicyInactiveByPolicyStatus(policy?.status)) {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(true);
      setSelectedPolicy({});
      reset();
    }
  }, [selectedOption]);

  const onSubmit = (data) => {
    // screenScrollTo();
    setFormStep(2);
    setFACFormData((currentFormData) => {
      return {
        ...currentFormData,
        ...data,
      };
    });
  };
  async function fetchDataPolicyData(policyNumber) {
    if (!policyNumber) {
      setSelectedPolicy({});
      reset();
    } else {
      let data = policies?.find((p) => p.contractID === policyNumber);
      if (data) {
        setSelectedPolicy(data);
      }
    }
  }

  useEffect(() => {
    if (Object.values(selectedPolicy).length) {
      const owners = getAllOwnersFromParties(selectedPolicy.parties);
      const loggedUser = owners?.find(
        (x) => x.person?.taxId === user.identityNumber
      );
      if (
        !loggedUser?.person?.firstName ||
        loggedUser?.person?.firstName === ""
      )
        setIsCorporationUserLoggedIn(true);
      setLoggedUser(loggedUser);
      if (isEntityUser(loggedUser?.person?.taxType)) {
        setValue(
          "ownerName",
          loggedUser?.person?.extendedName
            ? loggedUser?.person?.extendedName
            : loggedUser?.person?.lastName
        );
      } else {
        setValue("ownerFirstName", loggedUser?.person.firstName);
        setValue("ownerLastName", loggedUser?.person.lastName);
      }
    } else setIsCorporationUserLoggedIn(false);
  }, [selectedPolicy]);

  const maritalStatus = watch("marital");
  const foreignDeath = watch("foreign");
  const ownerName = watch("ownerName");

  return (
    <>
      {formStep === 1 && (
        <form onSubmit={FACStepOneHandleSubmit(onSubmit)}>
          <p
            id={`${uuid}-pn-note`}
            className="text-xs text-red-600 font-bold mb-8 italic"
          >
            {t("FACStep-one-subtitle")}
          </p>
          <h2 className="flex flex-grow font-body font-bold text-lg mb-2 tracking-tight">
            {t("FACStep-one-policy-information")}
          </h2>
          <div className="grid gap-4 grid-cols-1 lg:gap-6">
            <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,1fr]">
              {user?.userId ? (
                <>
                  <Controller
                    name="policyNumber"
                    control={control}
                    render={() => (
                      <RELEASING_AutoCompleteComboBox
                        labelName={t("FACStep-one-policy-number")}
                        tooltipVerbiage={t(
                          "CustomerFACStepOne-tooltip-verbiage"
                        )}
                        inputPlaceholderText={t("FACStep-one-select-policy")}
                        options={policiesToChooseFrom}
                        selectedOption={selectedOption}
                        setSelectedOption={setSelectedOption}
                        readOnly={policiesToChooseFrom.length <= 2}
                        useSmallFont={true}
                        showBadge={true}
                      />
                    )}
                    rules={{
                      required: `Please select a contract number`,
                    }}
                  />

                  <FormInputError
                    id={`${uuid}-pn-error`}
                    errorMessage={errors.policyNumber?.message}
                  />
                </>
              ) : (
                <></>
              )}
            </div>
            <div
              className={`grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 ${
                ownerName || isCorporationUserLoggedIn
                  ? "md:grid-cols-[1fr]"
                  : "md:grid-cols-[1fr,1fr]"
              }`}
            >
              {ownerName ? (
                <div>
                  <label htmlFor={`${uuid}-owner-name`} className="block mb-1">
                    {t("FACStep-one-ownername")}
                  </label>
                  <input
                    id={`${uuid}-owner-name`}
                    type="text"
                    title={ownerName}
                    placeholder="Policy Owner Name"
                    readOnly
                    {...FACStepOneRegister("ownerName")}
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor={`${uuid}-owner-fn`} className="block mb-1">
                      {t("FACStep-one-owner-fn")}
                    </label>
                    <input
                      id={`${uuid}-owner-fn`}
                      type="text"
                      placeholder="First Name"
                      disabled
                      {...FACStepOneRegister("ownerFirstName")}
                    />
                  </div>
                  <div>
                    <label htmlFor={`${uuid}-owner-ln`} className="block mb-1">
                      {t("FACStep-one-owner-ln")}
                    </label>
                    <input
                      id={`${uuid}-owner-ln`}
                      type="text"
                      placeholder="Last Name"
                      disabled
                      {...FACStepOneRegister("ownerLastName")}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <h2 className="flex flex-grow font-body font-bold text-lg mb-2 mt-8 tracking-tight">
            {t("FACStep-one-deceased-information")}
          </h2>
          <div className="grid gap-4 grid-cols-1 lg:gap-6">
            <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[2fr,2fr,1fr]">
              <div>
                <label htmlFor={`${uuid}-fn`} className="block mb-1">
                  {t("FACStep-one-fn")}
                </label>
                <input
                  id={`${uuid}-fn`}
                  type="text"
                  disabled={isDisabled}
                  placeholder="First Name"
                  {...FACStepOneRegister("firstName", {
                    required: "Enter First Name",
                  })}
                />
                <FormInputError
                  id={`${uuid}-fn-error`}
                  errorMessage={errors.firstName?.message}
                />
              </div>

              <div>
                <label htmlFor={`${uuid}-ln`} className="block mb-1">
                  {t("FACStep-one-ln")}
                </label>
                <input
                  id={`${uuid}-ln`}
                  type="text"
                  disabled={isDisabled}
                  placeholder="Last Name"
                  {...FACStepOneRegister("lastName", {
                    required: "Enter Last name",
                  })}
                />
                <FormInputError
                  id={`${uuid}-ln-error`}
                  errorMessage={errors.lastName?.message}
                />
              </div>

              <div>
                <label htmlFor={`${uuid}-mn`} className="block mb-1">
                  {t("FACStep-one-middle-initial")}
                </label>
                <input
                  id={`${uuid}-mn`}
                  type="text"
                  disabled={isDisabled}
                  {...FACStepOneRegister("middleName")}
                />
              </div>
            </div>

            <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,1fr,1fr]">
              <div>
                <label htmlFor={`${uuid}-dob`} className="block mb-1">
                  {t("FACStep-one-dob")}
                </label>
                <input
                  id={`${uuid}-dob`}
                  type="date"
                  disabled={isDisabled}
                  {...FACStepOneRegister("dob", {
                    required: "Enter Date of Birth",
                  })}
                  max={moment().add(-1, "days").format("YYYY-MM-DD").toString()}
                />
                <FormInputError
                  id={`${uuid}-dob-error`}
                  errorMessage={errors.dob?.message}
                />
              </div>

              <div className="relative">
                <label id={`${uuid}-ssn-label`} className="block mb-1">
                  {t("FACStep-one-ssn")}
                </label>
                <SSNInput
                  name="ssn"
                  describedby={`${uuid}-ssn-label`}
                  register={FACStepOneRegister}
                  setValue={setValue}
                  length="full"
                  isDisabled={isDisabled}
                />

                <FormInputError
                  id={`${uuid}-ssn-error`}
                  errorMessage={errors.ssn?.message}
                />
              </div>

              <div>
                <Controller
                  name="insuredTaxState"
                  control={control}
                  render={() => (
                    <RELEASING_AutoCompleteComboBox
                      labelName={t("FACStep-one-state")}
                      inputPlaceholderText={t("FACStep-one-state-option")}
                      options={[
                        {
                          value: "all",
                          displayAs: t("FACStep-one-state-option"),
                          selectedPolicyDisplayAs: t(
                            "FACStep-one-state-option"
                          ),
                          isDisplayOnly: true,
                        },
                        ...states.map((state) => ({
                          value: state.abbreviation,
                          displayAs: state.name,
                          selectedPolicyDisplayAs: state.name,
                          isDisplayOnly: true,
                        })),
                      ]}
                      selectedOption={selectedState}
                      setSelectedOption={(option) => {
                        setSelectedState(option);
                        if (option?.value !== "all") {
                          setValue("insuredTaxState", option.value);
                        } else {
                          setValue("insuredTaxState", "");
                        }
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,2.05fr]">
              <div>
                <label htmlFor={`${uuid}-dod`} className="block mb-1">
                  {t("FACStep-one-dod")}
                </label>
                <input
                  id={`${uuid}-dod`}
                  type="date"
                  disabled={isDisabled}
                  max={moment().format("YYYY-MM-DD").toString()}
                  {...FACStepOneRegister("dod")}
                  onKeyUp={() => {
                    clearTimeout(debounceFunction);
                    debounceFunction = setTimeout(async () => {
                      if (
                        getValues("dod") &&
                        moment(getValues("dod")).format("YYYY-MM-DD") >
                          moment().format("YYYY-MM-DD")
                      )
                        setIsDateOfDeathValid(false);
                      else setIsDateOfDeathValid(true);
                    }, debounceTimer);
                  }}
                  onKeyDown={() => {
                    clearTimeout(debounceFunction);
                  }}
                />
                {!isDateOfDeathValid && (
                  <FormInputError
                    errorMessage={"Please select valid date of death."}
                  />
                )}
                <div className="inline-block mt-2 mr-6">
                  <label className="inline-flex items-center text-xs font-normal">
                    <input
                      id={`${uuid}-unknown`}
                      type="checkbox"
                      className="mr-1"
                      disabled={isDisabled}
                      {...FACStepOneRegister("dodUnknown")}
                    />
                    {t("FACStep-one-unknown")}
                  </label>
                </div>
              </div>
              <div className="mt-4 ">
                <fieldset>
                  <legend>{t("FACStep-one-mod")} </legend>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-manner1`}
                        type="radio"
                        name="manner"
                        className="mr-1"
                        value="Natural"
                        disabled={isDisabled}
                        {...FACStepOneRegister("manner")}
                      />{" "}
                      {t("FACStep-one-natural")}
                    </label>
                  </div>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-manner2`}
                        type="radio"
                        disabled={isDisabled}
                        name="manner"
                        className="mr-1"
                        value="Accidental"
                        {...FACStepOneRegister("manner")}
                      />{" "}
                      {t("FACStep-one-accidental")}
                    </label>
                  </div>

                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-manner3`}
                        type="radio"
                        disabled={isDisabled}
                        name="manner"
                        className="mr-1"
                        value="other"
                        {...FACStepOneRegister("manner")}
                      />
                      {t("FACStep-one-other")}
                    </label>
                  </div>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-manner4`}
                        type="radio"
                        disabled={isDisabled}
                        name="manner"
                        className="mr-1"
                        value="Unknown"
                        {...FACStepOneRegister("manner")}
                      />
                      {t("FACStep-one-unknown-label")}
                    </label>
                  </div>
                </fieldset>
              </div>
            </div>

            <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1.5fr,2.5fr]">
              <div>
                <fieldset>
                  <legend>{t("FACStep-one-dsc")} </legend>

                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-sm font-normal">
                      <input
                        id={`${uuid}-foreign-1`}
                        type="radio"
                        className="mr-1"
                        disabled={isDisabled}
                        value="yes"
                        {...FACStepOneRegister("foreign")}
                      />
                      {t("FACStep-one-dsc-label-1")}
                    </label>
                  </div>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-sm font-normal">
                      <input
                        id={`${uuid}-foreign-2`}
                        type="radio"
                        className="mr-1"
                        disabled={isDisabled}
                        value="no"
                        {...FACStepOneRegister("foreign")}
                      />
                      {t("FACStep-one-dsc-label-2")}
                    </label>
                  </div>
                </fieldset>
              </div>
              <div>
                {foreignDeath === "yes" && (
                  <>
                    <label
                      htmlFor={`${uuid}-foreignCountry`}
                      className="block mb-1"
                    >
                      {t("FACStep-one-dsc-1")}
                    </label>
                    <input
                      id={`${uuid}-foreignCountry`}
                      disabled={isDisabled}
                      {...FACStepOneRegister("foreignCountry")}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[2fr,2fr]">
              <div className="">
                <fieldset>
                  <legend className="font-bold">
                    {t("FACStep-one-marital-status")}{" "}
                  </legend>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-marital1`}
                        type="radio"
                        disabled={isDisabled}
                        name="marital"
                        className="mr-1"
                        value="married"
                        {...FACStepOneRegister("marital")}
                      />{" "}
                      {t("FACStep-one-marital-label-1")}
                    </label>
                  </div>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-marital2`}
                        type="radio"
                        name="marital"
                        disabled={isDisabled}
                        className="mr-1"
                        value="widowed"
                        {...FACStepOneRegister("marital")}
                      />
                      {t("FACStep-one-marital-label-2")}
                    </label>
                  </div>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-marital3`}
                        type="radio"
                        name="marital"
                        disabled={isDisabled}
                        className="mr-1"
                        value="unknown"
                        {...FACStepOneRegister("marital")}
                      />{" "}
                      {t("FACStep-one-marital-label-3")}
                    </label>
                  </div>
                </fieldset>
              </div>
              {maritalStatus === "married" && (
                <div>
                  <label htmlFor={`${uuid}-spouse-name`} className="block mb-1">
                    {t("FACStep-one-name-spouse")}
                  </label>
                  <input
                    id={`${uuid}-spouse-name`}
                    disabled={isDisabled}
                    {...FACStepOneRegister("spouseName")}
                  />
                </div>
              )}
              {maritalStatus === "widowed" && (
                <div>
                  <label
                    htmlFor={`${uuid}-widowed-name`}
                    className="block mb-1"
                  >
                    {t("FACStep-one-name-deceased-spouse")}
                  </label>
                  <input
                    id={`${uuid}-widowed-name`}
                    disabled={isDisabled}
                    {...FACStepOneRegister("deceasedSpouse")}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              className="ui-btn-primary text-center"
              disabled={isDisabled}
            >
              {t("FACStep-one-button")}
            </button>
          </div>
        </form>
      )}
      <GeneralPurposeDialog
        state={openDialog}
        setState={setOpenDialog}
        title={"Inactive Contracts are Ineligible for Claims"}
        enablePrimaryButton={true}
        enableSecondaryButton={false}
        primaryButtonText={"Close"}
        dialogOnClose={() => null}
        primaryButtonAction={() => setOpenDialog(false)}
        width="max-w-2xl"
        desc={
          <Trans
            i18nKey={"CustomerFACStepOne-error-message"}
            components={{
              link1: (
                <Link
                  className="text-ui-blue underline"
                  to="/guest/help"
                ></Link>
              ),
            }}
          />
        }
      />
    </>
  );
}
